// This slot lives apart from `slots.tsx` to keep an import cycle out of the folder: `slots.tsx`
// renders `InternalFileItem`, and `InternalFileItem` renders this slot. Importing anything from
// `./slots` here, or reaching this slot through `./slots` from `InternalFileItem`, restores the cycle.
import { Button } from '@magicschool/spellbook/components/Button';
import { FileItem } from '@magicschool/spellbook/components/FileItem';
import { Modal } from '@magicschool/spellbook/components/Modal';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import type React from 'react';
import type { ConfirmRemoveText } from './types';

// Re-exported for the callers that already import it from here. `types.ts` imports nothing that
// reaches a component, so sourcing the type there cannot revive the cycle.
export type { ConfirmRemoveText };

interface DropzoneRemoveConfirmProps {
  fileName: string;
  onRemove: () => void;
  disabled?: boolean;
  text?: ConfirmRemoveText;
  dropzoneRef?: React.RefObject<HTMLElement | null>;
  /**
   * Skip the confirmation modal and remove the file immediately on click.
   * Useful when Dropzone lives inside an overlay (e.g. Drawer, Modal)
   * or when files are staged but not yet persisted.
   */
  noConfirm?: boolean;
}

/** Remove action with configurable confirmation. Slot allows full structural override. */
export const DropzoneRemoveConfirmSlot = Slot<DropzoneRemoveConfirmProps>({
  displayName: 'Dropzone.RemoveConfirm',
  renderDefault: ({ fileName, onRemove, disabled, text, dropzoneRef, noConfirm }) => {
    const handleRemove = () => {
      onRemove();
      requestAnimationFrame(() => {
        dropzoneRef?.current?.focus();
      });
    };

    const removeTooltip = text?.removeTooltip ?? 'Remove';

    if (noConfirm) {
      return (
        <Tooltip mode="label">
          <Tooltip.Trigger>
            <FileItem.RemoveAction
              data-spellbook-name="dropzone-remove-confirm"
              size="md"
              onClick={disabled ? undefined : handleRemove}
              disabled={disabled}
            />
          </Tooltip.Trigger>
          <Tooltip.Content>
            {removeTooltip}
            <span className="sr-only"> {fileName}</span>
          </Tooltip.Content>
        </Tooltip>
      );
    }

    const title = text?.confirmTitle ?? 'Remove file?';
    const description = (text?.confirmDescription ?? 'Are you sure you want to remove "{fileName}"?').replace('{fileName}', fileName);
    const confirmLabel = text?.confirmButton ?? 'Remove';
    const cancelLabel = text?.cancelButton ?? 'Cancel';

    return (
      <Modal>
        <Modal.Tooltip mode="label">
          <Modal.Trigger>
            <FileItem.RemoveAction data-spellbook-name="dropzone-remove-confirm" size="md" disabled={disabled} />
          </Modal.Trigger>
          <Modal.TooltipContent>
            {removeTooltip}
            <span className="sr-only"> {fileName}</span>
          </Modal.TooltipContent>
        </Modal.Tooltip>
        <Modal.Header>{title}</Modal.Header>
        <Modal.Description>{description}</Modal.Description>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button color="error" onClick={handleRemove}>
              {confirmLabel}
              <span className="sr-only"> {fileName}</span>
            </Button>
          </Modal.Close>
          <Modal.Close asChild>
            <Button variant="outlined">{cancelLabel}</Button>
          </Modal.Close>
        </Modal.Footer>
      </Modal>
    );
  },
});
